import React, { useState } from 'react';
import { connect } from 'react-redux';
import loadable from '@loadable/component';
import { Container, Row, Col } from 'reactstrap';

import {
  usePage,
  useTealium,
  useAdGlare,
  useUnmountResetPage,
  useDebounceLoading,
  useDelayJsLoadiing,
  useURLChangeCallback,
} from '../../utils/hookHelper';

import HelmetMeta from '../../components/HelmetMeta';
import HelmetTitle from '../../components/HelmetTitle';
import HelmetStyle from '../../components/HelmetStyle';
import HelmetScript from '../../components/HelmetScript';
import HelmetHead from '../../components/HelmetHead';
import Loader from '../../components/Loader';
import UnderConstruction from '../../components/UnderConstruction';
import { parseSanitizedHTML } from '../../utils/Helper';
import config from '../../config';

import './HomePage.scss';

// components
const ImageSlider = loadable(() => import('../../widgets/ImageSlider'));
const IndexCards = loadable(() => import('../../widgets/IndexCards'));
const LinksCard = loadable(() => import('../../widgets/LinksCard'));
const VideoCards = loadable(() => import('../../widgets/VideoCards'));
const SocialMediaIcons = loadable(() => import('../../widgets/SocialMediaIcons'));
const EventTable = loadable(() => import('../../widgets/EventTable'));

const Home = (props) => {
  const { i18n, homeData, allowCookies, enableTealium, enableAdGlare, siteConfig } = props;
  const debouncePageLoading = useDebounceLoading(homeData);
  const jsLoading = useDelayJsLoadiing(homeData);

  usePage('home', config.pageType.homePage);
  useAdGlare(i18n.locale, siteConfig, homeData, enableAdGlare.status);
  useUnmountResetPage();

  const siteConfigData = siteConfig.data || {};
  const { defaultLanguage } = siteConfigData;

  const { translations = {}, locale = defaultLanguage } = i18n || {};
  const t = translations[locale] || {};
  const { data = {} } = homeData || {};
  const content = ((data || {}).content || {}).content || {};
  const dataContent = content[locale] || {};
  const { under_construction = false } = content;

  // content
  const {
    jumbotron = {},
    indexCards = {},
    eventTable = {},
    linksCard = {},
    videoCards = {},
    socialMediaIcons = {},
    main_paragraph1 = '',
    main_paragraph2 = '',
    main_paragraph3 = '',
    main_pUnderBanner = '',
  } = dataContent || {};

  // index
  const indexTitle = indexCards.title || '';
  const indexDisplayAsSlider = indexCards.displayAsSlider || false;
  const indexMaxCardsInSlider = indexCards.maxCardsInSlider || '';
  const indexQuery = indexCards.query || '';
  const indexUrl = indexCards.url || '';
  const indexSortBy = indexCards.sortBy;
  const indexStatus = indexCards.displayStatus || false;

  // event
  const eventTitle = eventTable.title || '';
  const eventUrl = eventTable.url || '';

  // link
  const { enableLinks } = linksCard;
  const linkImage = linksCard.image || '';
  const linkImageAlt = linksCard.alt || '';
  const linkTitle = linksCard.title || '';
  const linkPage = linksCard.pageName || '';
  const linkUrl = linksCard.url || '';
  const noOfLinks = linksCard.noOfLinks || '';

  // video
  const videoTitle = videoCards.title || '';
  const videos = videoCards.videos || [];
  const videoUrl = videoCards.url || '';

  // video
  const socialMediaTitle = socialMediaIcons.title || '';
  const icons = socialMediaIcons.icons || [];

  // HelmetMeta
  const { metadata = {} } = data || {};
  const publishDate = metadata['TeamSite/LiveSite/PublishDate'];
  const seo = dataContent.seo || {};
  const { css_style_block = '', js_script_block = '', extra_head = '' } = content;

  const title = seo.seo_title;
  const [domTitle, setDomTitle] = useState('');
  const [isScriptReady, setIsScriptReady] = useState(false);

  useTealium(
    i18n.locale,
    siteConfig,
    homeData,
    allowCookies.status,
    enableTealium.status,
    domTitle,
  );

  useURLChangeCallback(locale, data, null, [], isScriptReady);

  const helmetTitleCallBack = (newState) => {
    setDomTitle(newState.title);
  };

  const helmetScriptCallBack = (isReady) => {
    setIsScriptReady(isReady);
  };

  const middleContent1 = main_paragraph1.length > 0 && main_paragraph1 !== '' && (
    <section className="section-mainPage-middleContent1">
      <Container>
        <Row>
          <Col>
            <div>{parseSanitizedHTML(main_paragraph1)}</div>
          </Col>
        </Row>
      </Container>
    </section>
  );
  const middleContent2 = main_paragraph2.length > 0 && main_paragraph2 !== '' && (
    <section className="section-mainPage-middleContent2">
      <Container>
        <Row>
          <Col>
            <div>{parseSanitizedHTML(main_paragraph2)}</div>
          </Col>
        </Row>
      </Container>
    </section>
  );
  const middleContent3 = main_paragraph3.length > 0 && main_paragraph3 !== '' && (
    <section className="section-mainPage-middleContent3">
      <Container>
        <Row>
          <Col>
            <div>{parseSanitizedHTML(main_paragraph3)}</div>
          </Col>
        </Row>
      </Container>
    </section>
  );
  const middleContentSNS = main_pUnderBanner.length > 0 && main_pUnderBanner !== '' && (
    <section className="section-mainPage-middleContentSNS">
      <Container>
        <Row>
          <Col>
            <div>{parseSanitizedHTML(main_pUnderBanner)}</div>
          </Col>
        </Row>
      </Container>
    </section>
  );

  const mainContent = (
    <>
      {middleContent1}
      <ImageSlider images={jumbotron} />
      {middleContentSNS}
      {indexStatus && (
        <IndexCards
          title={indexTitle}
          query={indexQuery}
          url={indexUrl}
          sortBy={indexSortBy}
          maxCardsInSlider={indexMaxCardsInSlider}
          displayAsSlider={indexDisplayAsSlider}
        />
      )}
      <EventTable title={eventTitle} url={eventUrl} />
      <VideoCards title={videoTitle} videos={videos} url={videoUrl} />
      {enableLinks && (
        <LinksCard
          title={linkTitle}
          pageName={linkPage}
          url={linkUrl}
          image={linkImage}
          alt={linkImageAlt}
          noOfLinks={noOfLinks}
        />
      )}
      {middleContent2}
      {<SocialMediaIcons title={socialMediaTitle} icons={icons} />}
      {middleContent3}
    </>
  );

  const pageEle = (
    <div className="mainPage">
      {under_construction && <UnderConstruction />}
      <h1 className="access-elem">{seo.seo_title}</h1>
      {!under_construction && mainContent}
      {!under_construction && (
        <HelmetScript
          helmetScriptCallBack={helmetScriptCallBack}
          extraHead={extra_head}
          jsScript={js_script_block}
        />
      )}
    </div>
  );

  return (
    <>
      {debouncePageLoading || jsLoading ? <Loader /> : pageEle}
      {!debouncePageLoading && <HelmetHead extraHead={extra_head} />}
      {!debouncePageLoading && <HelmetStyle stylesheet={css_style_block} />}
      {!debouncePageLoading && <HelmetTitle title={title} cb={helmetTitleCallBack} />}
      {!debouncePageLoading && (
        <HelmetMeta seo={seo} publishDate={publishDate} pageClass="homePage" />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  i18n: state.i18n,
  homeData: state.generalPage,
  allowCookies: state.allowCookies,
  enableTealium: state.enableTealium,
  enableAdGlare: state.enableAdGlare,
  siteConfig: state.siteConfig,
});

const connectStore = connect(mapStateToProps, null, null, {
  pure: true,
})(Home);

export { Home as HomeComponent }; // <- For component Unit Test
export default connectStore;
